import "./address-book-address.css";

import { useQuery } from "@tanstack/react-query";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import type {
	GridColumnProps,
	GridCustomCellProps,
} from "@tql/ui-components-react/kendo/grid";
import { checkVerified_01Solid } from "@tql/ui-components-react/untitled/icons";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { sortAndFormatAccessorials } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { UserAvatar } from "~/shared/components/navbar/components/user-avatar/user-avatar.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import { accessorialSchema } from "~/shared/schemas/accessorial-schema.ts";
import type {
	AddressBookContactDetail,
	AddressBookItemWithFavorite,
	AddressBookUpdateBodyV2,
} from "~/shared/schemas/address-book-schema.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import {
	formatTimeToString,
	getTimeZoneShortName,
	parseTimeString,
} from "~/shared/utils/time-utils.ts";

import type { UnifiedHistoryItem } from "../../hooks/use-address-book-history.ts";
import {
	formatTimestamp,
	useAddressBookHistory,
} from "../../hooks/use-address-book-history.ts";
import {
	address1Validator,
	AddressFormFields,
} from "../address-form-fields/address-form-fields.tsx";
import { AddressBookMissingItem } from "./components/address-book-missing-item.tsx";
import { AddressBookPickupDeliveryCard } from "./components/address-book-pickup-delivery-card/address-book-pickup-delivery-card.tsx";

export type AddressBookAddressSubmitData = {
	addressBookUpdate: AddressBookUpdateBodyV2;
	isPickupDefault: boolean;
	isDeliveryDefault: boolean;
	isLegacy: boolean;
};

type AddressBookAddressProps = {
	addressBookItem: AddressBookItemWithFavorite | null | undefined;
	onSubmit: (data: AddressBookAddressSubmitData) => void;
};

export type AddressBookAddressRef = {
	submitForm: () => void;
};

const columns: GridColumnProps[] = [
	{
		field: "validFrom",
		title: "Change Date / Time",
		width: 200,
		cells: {
			data: (props: GridCustomCellProps) => {
				const dataItem = props.dataItem as UnifiedHistoryItem;
				return (
					<GridTd tdProps={props.tdProps}>
						{formatTimestamp(dataItem.validFrom)}
					</GridTd>
				);
			},
		},
	},
	{
		field: "createdByInternalName",
		title: "Change Made By",
		width: 200,
		cells: {
			data: (props: GridCustomCellProps) => {
				const dataItem = props.dataItem as UnifiedHistoryItem;
				if (!dataItem.createdByInternalName)
					return <GridTd tdProps={props.tdProps} />;
				return (
					<GridTd tdProps={props.tdProps}>
						<div className={"changed-by-cell"}>
							<UserAvatar
								name={dataItem.createdByInternalName ?? "Unknown"}
								size={"small"}
							/>
							<span className={"tql-link"}>
								{dataItem.createdByInternalName}
							</span>
							{dataItem.isAR && dataItem.isLTL ? (
								<span className={"changed-by-cell-title"}>LTL AR</span>
							) : null}
						</div>
					</GridTd>
				);
			},
		},
	},
	{ field: "changes", title: "Changes" },
];

const getAccessorials = (
	detail: AddressBookContactDetail | undefined,
	allAccessorials: Accessorial[] | undefined,
): Accessorial[] => {
	if (!detail?.accessorials || !allAccessorials) return [];

	try {
		// Parse the JSON string to get the array of accessorial IDs
		const accessorialIds = JSON.parse(detail.accessorials) as number[];
		if (Array.isArray(accessorialIds) && accessorialIds.length > 0) {
			return allAccessorials.filter((a) => accessorialIds.includes(a.id));
		}
	} catch (error) {
		console.error("Failed to parse accessorials:", error);
	}

	return [];
};

type AddressBookFormValues = {
	companyName: string;
	address1: string;
	address2: string;
	city: string;
	state: string;
	addressPostalCode: ZipCode | null;
	country: string;
	isPickupDefault: boolean;
	isDeliveryDefault: boolean;
	// Pickup fields (stopType 0)
	pickup_contactName: string;
	pickup_contactPhone: string;
	pickup_contactExtension: string;
	pickup_hoursOpen: Date | null;
	pickup_hoursClose: Date | null;
	pickup_specialInstructions: string;
	pickup_accessorials: Accessorial[];
	// Delivery fields (stopType 1)
	delivery_contactName: string;
	delivery_contactPhone: string;
	delivery_contactExtension: string;
	delivery_hoursOpen: Date | null;
	delivery_hoursClose: Date | null;
	delivery_specialInstructions: string;
	delivery_accessorials: Accessorial[];
};

const isStopBlank = (
	prefix: "pickup" | "delivery",
	valueGetter: (field: string) => unknown,
) => {
	const contactName = valueGetter(`${prefix}_contactName`) as string;
	const contactPhone = valueGetter(`${prefix}_contactPhone`) as string;
	const contactExtension = valueGetter(`${prefix}_contactExtension`) as string;
	const hoursOpen = valueGetter(`${prefix}_hoursOpen`) as Date | null;
	const hoursClose = valueGetter(`${prefix}_hoursClose`) as Date | null;
	const specialInstructions = valueGetter(
		`${prefix}_specialInstructions`,
	) as string;
	const accessorials = valueGetter(`${prefix}_accessorials`) as Accessorial[];

	return (
		!contactName?.trim() &&
		!contactPhone?.trim() &&
		!contactExtension?.trim() &&
		!hoursOpen &&
		!hoursClose &&
		!specialInstructions?.trim() &&
		(!accessorials || accessorials.length === 0)
	);
};

const formValidator = (values: AddressBookFormValues) => {
	const errors: Record<string, string> = {};

	if (!values.companyName?.trim()) {
		errors["companyName"] = "Company Name is required";
	}
	const address1Error = address1Validator(values.address1);
	if (address1Error) {
		errors["address1"] = address1Error;
	}
	if (!values.addressPostalCode) {
		errors["addressPostalCode"] = "Postal Code is required";
	}
	if (!values.city?.trim()) {
		errors["city"] = "City is required";
	}
	if (!values.state?.trim()) {
		errors["state"] = "State is required";
	}
	if (!values.country?.trim()) {
		errors["country"] = "Country is required";
	}

	return errors;
};

const getStopDetail = (
	prefix: "pickup" | "delivery",
	formValues: AddressBookFormValues,
	existingDetail?: AddressBookContactDetail,
) => {
	const contactName = formValues[
		`${prefix}_contactName` as keyof AddressBookFormValues
	] as string;
	const contactPhone = formValues[
		`${prefix}_contactPhone` as keyof AddressBookFormValues
	] as string;
	const contactExtension = formValues[
		`${prefix}_contactExtension` as keyof AddressBookFormValues
	] as string;
	const hoursOpen = formValues[
		`${prefix}_hoursOpen` as keyof AddressBookFormValues
	] as Date | null;
	const hoursClose = formValues[
		`${prefix}_hoursClose` as keyof AddressBookFormValues
	] as Date | null;
	const specialInstructions = formValues[
		`${prefix}_specialInstructions` as keyof AddressBookFormValues
	] as string;
	const accessorials = formValues[
		`${prefix}_accessorials` as keyof AddressBookFormValues
	] as Accessorial[];

	const isBlank =
		!contactName?.trim() &&
		!contactPhone?.trim() &&
		!contactExtension?.trim() &&
		!hoursOpen &&
		!hoursClose &&
		!specialInstructions?.trim() &&
		(!accessorials || accessorials.length === 0);

	if (isBlank) return null;

	return {
		contactDetailId: existingDetail?.contactDetailId,
		addressBookItemId: existingDetail?.addressBookItemId,
		contactName,
		contactPhone,
		contactExtension,
		hoursOpen: formatTimeToString(hoursOpen) || undefined,
		hoursClose: formatTimeToString(hoursClose) || undefined,
		stopType: prefix === "pickup" ? 1 : 2,
		mode: existingDetail?.mode,
		timeZone:
			hoursOpen || hoursClose
				? getTimeZoneShortName()
				: (existingDetail?.timeZone ?? undefined),
		specialInstructions: specialInstructions || undefined,
		accessorials: JSON.stringify(accessorials.map((a) => a.id)),
	};
};

export const AddressBookAddress = forwardRef<
	AddressBookAddressRef,
	AddressBookAddressProps
>(({ addressBookItem, onSubmit }, ref) => {
	const {
		isReady: traxLtlLegacyFlipIsReady,
		isEnabled: traxLtlLegacyFlipIsEnabled,
	} = useFeatureFlagGate("traxLtlLegacyFlip");
	const formRenderPropsRef = useRef<FormRenderProps | null>(null);

	const { data: allAccessorials, isLoading: isLoadingAccessorials } = useQuery({
		queryKey: ["accessorials"],
		queryFn: async () => {
			const result = await getAndValidate(
				apiRoutes.ACCESSORIALS,
				accessorialSchema.array(),
			);
			return result.success ? result.data : [];
		},
		staleTime: 1000 * 60 * 60, // 1 hour
	});

	const { historyData, isLoadingHistory } = useAddressBookHistory(
		addressBookItem?.addressBookItemId,
	);

	const groupedAccessorials = useMemo(() => {
		if (!allAccessorials) return null;
		return sortAndFormatAccessorials(allAccessorials);
	}, [allAccessorials]);

	useImperativeHandle(ref, () => ({
		submitForm: () => {
			if (addressBookItem && formRenderPropsRef.current) {
				formRenderPropsRef.current.onSubmit({} as React.SyntheticEvent);
			}
		},
	}));

	useAutoSpinner(isLoadingAccessorials || isLoadingHistory);

	const navigate = useNavigate();
	const { customerId } = useParams<{ customerId?: string }>();

	const handleMissingItemRetry = (): void => {
		if (customerId) {
			navigate(
				routes.addressBookWithCustomer.replace(":customerId", customerId),
			);
			return;
		}

		navigate(routes.addressBook);
	};

	if (!addressBookItem) {
		return <AddressBookMissingItem onRetry={handleMissingItemRetry} />;
	}

	const isLegacy = addressBookItem.isLegacy;

	const pickupDetail = addressBookItem.details.find((d) => d.stopType === 1);
	const deliveryDetail = addressBookItem.details.find((d) => d.stopType === 2);

	const initialValues: AddressBookFormValues = {
		companyName: addressBookItem.companyName ?? "",
		address1: addressBookItem.address1 ?? "",
		address2: addressBookItem.address2 ?? "",
		city: addressBookItem.city ?? "",
		state: addressBookItem.stateProvince ?? "",
		addressPostalCode: addressBookItem.postalCode
			? ({
					postalCode: addressBookItem.postalCode,
					selectablePostalCode: addressBookItem.postalCode,
				} as ZipCode)
			: null,
		country: addressBookItem.country ?? "",
		isPickupDefault: addressBookItem.isPickupDefault,
		isDeliveryDefault: addressBookItem.isDeliveryDefault,
		// Pickup fields
		pickup_contactName: pickupDetail?.contactName ?? "",
		pickup_contactPhone: pickupDetail?.contactPhone ?? "",
		pickup_contactExtension: pickupDetail?.contactExtension ?? "",
		pickup_hoursOpen: parseTimeString(pickupDetail?.hoursOpen),
		pickup_hoursClose: parseTimeString(pickupDetail?.hoursClose),
		pickup_specialInstructions: pickupDetail?.specialInstructions ?? "",
		pickup_accessorials: getAccessorials(pickupDetail, allAccessorials),
		// Delivery fields
		delivery_contactName: deliveryDetail?.contactName ?? "",
		delivery_contactPhone: deliveryDetail?.contactPhone ?? "",
		delivery_contactExtension: deliveryDetail?.contactExtension ?? "",
		delivery_hoursOpen: parseTimeString(deliveryDetail?.hoursOpen),
		delivery_hoursClose: parseTimeString(deliveryDetail?.hoursClose),
		delivery_specialInstructions: deliveryDetail?.specialInstructions ?? "",
		delivery_accessorials: getAccessorials(deliveryDetail, allAccessorials),
	};

	const handleFormSubmit = (values: Record<string, unknown>): void => {
		const formValues = values as unknown as AddressBookFormValues;

		const details = [];

		const pickup = getStopDetail("pickup", formValues, pickupDetail);
		if (pickup) details.push(pickup);

		const delivery = getStopDetail("delivery", formValues, deliveryDetail);
		if (delivery) details.push(delivery);

		const updateBody: AddressBookUpdateBodyV2 = {
			addressBookItemId: addressBookItem.addressBookItemId,
			customerId: addressBookItem.customerId,
			companyName: formValues.companyName,
			address1: formValues.address1,
			address2: formValues.address2,
			postalCode: formValues.addressPostalCode?.postalCode ?? "",
			city: formValues.city,
			stateProvince: formValues.state,
			country: formValues.country,
			isValidated: addressBookItem.isValidated ?? undefined,
			legacyAddressId: addressBookItem.isLegacy
				? addressBookItem.addressBookItemId
				: (addressBookItem.legacyAddressId ?? undefined),
			details,
		};

		onSubmit({
			addressBookUpdate: updateBody,
			isPickupDefault: pickup ? formValues.isPickupDefault : false,
			isDeliveryDefault: delivery ? formValues.isDeliveryDefault : false,
			isLegacy: addressBookItem.isLegacy,
		});
	};

	return (
		<div className={"address-book-address-container"}>
			{isLegacy && traxLtlLegacyFlipIsReady && traxLtlLegacyFlipIsEnabled ? (
				<div className={"legacy-edit-info"}>
					<p>
						This is a Legacy address. Saving changes will convert it to an
						address in the New TRAX Address Book.
					</p>
				</div>
			) : null}
			<Form
				key={`${addressBookItem.addressBookItemId}-${allAccessorials ? "loaded" : "loading"}`}
				ignoreModified={true}
				initialValues={initialValues}
				onSubmit={handleFormSubmit}
				render={(formRenderProps: FormRenderProps) => {
					formRenderPropsRef.current = formRenderProps;
					return (
						<FormElement className={"address-book-address-form-wrapper"}>
							<div
								className={"address-book-location-section address-book-section"}
								data-testid={"address-book-location-details-section"}
							>
								<div className={"address-book-section-header"}>Location</div>
								<div className={"address-book-location-content-wrapper"}>
									<div className={"address-book-location-form"}>
										<AddressFormFields
											disabled={
												isLegacy && traxLtlLegacyFlipIsReady
													? !traxLtlLegacyFlipIsEnabled
													: undefined
											}
											formRenderProps={formRenderProps}
										/>
									</div>
									<div
										className={
											"address-book-location-validated-address-wrapper"
										}
									>
										<div
											className={
												"address-book-location-validated-address-header"
											}
										>
											{addressBookItem.isValidated ? (
												<>
													<SvgIcon
														className={"verify-check"}
														icon={checkVerified_01Solid}
														size={"large"}
													/>
													Validated Address
												</>
											) : null}
										</div>
										<div
											className={"address-book-location-validated-address"}
										></div>
									</div>
								</div>
							</div>
							<div className={"address-book-pickup-delivery-section"}>
								<AddressBookPickupDeliveryCard
									detail={pickupDetail}
									disabled={
										isLegacy && traxLtlLegacyFlipIsReady
											? !traxLtlLegacyFlipIsEnabled
											: undefined
									}
									fieldPrefix={"pickup"}
									isDefault={
										!isStopBlank("pickup", formRenderProps.valueGetter) &&
										(formRenderProps.valueGetter("isPickupDefault") as boolean)
									}
									isDefaultDisabled={isStopBlank(
										"pickup",
										formRenderProps.valueGetter,
									)}
									locationTypeOptions={
										groupedAccessorials?.locationType["pickup"] ?? []
									}
									onChange={(val: Accessorial[]) =>
										formRenderProps.onChange("pickup_accessorials", {
											value: val,
										})
									}
									onDefaultChange={(val: boolean) =>
										formRenderProps.onChange("isPickupDefault", {
											value: val,
										})
									}
									serviceOptions={
										groupedAccessorials?.selectable["pickup"] ?? []
									}
									type={"pickup"}
									value={
										formRenderProps.valueGetter(
											"pickup_accessorials",
										) as Accessorial[]
									}
								/>
								<AddressBookPickupDeliveryCard
									detail={deliveryDetail}
									disabled={
										isLegacy && traxLtlLegacyFlipIsReady
											? !traxLtlLegacyFlipIsEnabled
											: undefined
									}
									fieldPrefix={"delivery"}
									isDefault={
										!isStopBlank("delivery", formRenderProps.valueGetter) &&
										(formRenderProps.valueGetter(
											"isDeliveryDefault",
										) as boolean)
									}
									isDefaultDisabled={isStopBlank(
										"delivery",
										formRenderProps.valueGetter,
									)}
									locationTypeOptions={
										groupedAccessorials?.locationType["delivery"] ?? []
									}
									onChange={(val: Accessorial[]) =>
										formRenderProps.onChange("delivery_accessorials", {
											value: val,
										})
									}
									onDefaultChange={(val: boolean) =>
										formRenderProps.onChange("isDeliveryDefault", {
											value: val,
										})
									}
									serviceOptions={
										groupedAccessorials?.selectable["delivery"] ?? []
									}
									type={"delivery"}
									value={
										formRenderProps.valueGetter(
											"delivery_accessorials",
										) as Accessorial[]
									}
								/>
							</div>
						</FormElement>
					);
				}}
				validator={formValidator}
			/>
			<div className={"address-book-change-log-section"}>
				<div className={"address-book-section-header"}>
					Address Book Change Log
				</div>
				<div className={"address-book-change-log-wrapper"}>
					<TraxGrid
						columns={columns}
						data={historyData ?? []}
						style={{ minWidth: 1000 }}
					/>
				</div>
			</div>
		</div>
	);
});

AddressBookAddress.displayName = "AddressBookAddress";
